// =====================================================================
// Bordereaux.jsx — M-015 « Remise transporteur » (29-08-2026)
// =====================================================================
// Écran unique, pensé pour le PDA / téléphone au quai :
//   1. choisir le transporteur présent
//   2. Zeus récupère les bordereaux depuis EffiTrace (tout est coché)
//   3. l'appareil passe au chauffeur : nom, réserves, signature
//   4. la signature est déposée dans le cadre EffiTrace de CHAQUE page
//   5. onglet Archives : recherche client / n° / chauffeur, transporteur,
//      période — ou une date précise, seule ou en intervalle.
//   6. page UNIQUE (28-10-2026, Silviu : « ne polluons pas ») : la liste des
//      transporteurs EST le tableau des colis (contrôlés, à signer, heure auto),
//      « Générer + imprimer » (pour tous, par ligne, dans la fiche) et la
//      génération automatique dans la fiche (bordereaux/BordereauxAuto.jsx).
//
// Une signature = UNE remise (un chauffeur, un transporteur, un passage).
// L'opérateur vient du compte connecté, jamais d'une liste déroulante.
// =====================================================================

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { bordereaux as api, errMsg } from '../services/api.js';
import { useAuth } from '../hooks/useAuth.jsx';
import Breadcrumbs from '../components/Breadcrumbs.jsx';
import { C, teinte } from './bordereaux/theme.js';
import { ListeRemise, ReglagesAuto, AnnulerDialog } from './bordereaux/BordereauxAuto.jsx';

// Palette du module (validée sur la maquette PDA) et couleur par transporteur : bordereaux/theme.js.

const kg = (n) => `${(Number(n) || 0).toFixed(1)} kg`;
const dt = (d) => (d ? new Date(d).toLocaleString('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const jour = (d) => (d ? new Date(d).toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');

const S = {
  wrap: { maxWidth: 560, margin: '0 auto', padding: '0 0 90px' },
  card: { background: C.surface, border: `1px solid ${C.line}`, borderRadius: 10, padding: '12px 13px' },
  lbl: { display: 'block', fontSize: 10.5, letterSpacing: '.08em', textTransform: 'uppercase', color: C.ink3, fontWeight: 600, marginBottom: 5 },
  input: { width: '100%', padding: 12, border: `1.5px solid ${C.lineStrong}`, borderRadius: 9, background: C.surface, color: C.ink, font: 'inherit', fontSize: 16, boxSizing: 'border-box' },
  big: { display: 'block', width: '100%', padding: 15, borderRadius: 10, background: C.accent, color: C.accentInk, fontWeight: 600, fontSize: 16, textAlign: 'center', border: 0, cursor: 'pointer' },
  bigSec: { display: 'block', width: '100%', padding: 15, borderRadius: 10, background: C.surface, color: C.ink, border: `1.5px solid ${C.lineStrong}`, fontWeight: 500, fontSize: 16, textAlign: 'center', cursor: 'pointer' },
  mono: { fontFamily: 'ui-monospace, "IBM Plex Mono", monospace', fontVariantNumeric: 'tabular-nums' },
  k: { fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', color: C.ink3, fontWeight: 600 },
  v: { fontFamily: 'ui-monospace, monospace', fontSize: 20, fontWeight: 600, lineHeight: 1.15 },
  chip: (on) => ({ flex: '0 0 auto', padding: '6px 11px', borderRadius: 20, border: `1.5px solid ${on ? C.accent : C.lineStrong}`, fontSize: 12.5, fontWeight: on ? 600 : 500, whiteSpace: 'nowrap', background: on ? C.accent : C.surface, color: on ? C.accentInk : C.ink, cursor: 'pointer' }),
  err: { fontSize: 13, color: C.crit, background: C.critSoft, padding: '9px 12px', borderRadius: 8 },
  empty: { padding: '34px 20px', textAlign: 'center', color: C.ink3, fontSize: 13.5 },
};

const PERIODES = [
  { id: 'j', label: "Aujourd'hui", jours: 0 },
  { id: '7', label: '7 jours', jours: 7 },
  { id: '30', label: '30 jours', jours: 30 },
  { id: 'all', label: 'Tout', jours: null },
];

export default function Bordereaux() {
  const { can, user } = useAuth();
  const peutSigner = can('bordereaux.sign.all');
  const peutRegler = can('bordereaux.auto.all');
  // sortir sans signature (28-10-2026, temporaire) — avec un droit de lecture de la page (la liste à sortir vient de /pending)
  const peutSansSignature = can('bordereaux.sanssignature.all') && can(['bordereaux.read.all', 'bordereaux.sign.all', 'bordereaux.auto.all']);

  const archivesVisibles = can('bordereaux.sign.all') || can('bordereaux.read.all');   // le droit « Paramétrer » seul : la liste et les fiches, pas les archives
  const [onglet, setOnglet] = useState('remise');       // remise | archives
  const [etape, setEtape] = useState(1);                // 1..4
  const [carriers, setCarriers] = useState([]);
  const [carrier, setCarrier] = useState(null);
  const [items, setItems] = useState([]);
  const [coches, setCoches] = useState(() => new Set());
  const [chargement, setChargement] = useState('');
  const [erreur, setErreur] = useState('');
  const [preuve, setPreuve] = useState(null);
  const [fiche, setFiche] = useState(null);
  const [info, setInfo] = useState('');
  const suivi = useRef(0);   // suivi d'une génération lancée depuis la fiche ; quitter la fiche (n'importe comment) l'arrête
  useEffect(() => () => { suivi.current += 1; }, []);

  const [chauffeur, setChauffeur] = useState('');
  const [reserves, setReserves] = useState('');

  // ------------------------------------------------------------------
  // Données
  // ------------------------------------------------------------------
  const chargerCarriers = useCallback(async () => {
    try { setCarriers(await api.carriers()); } catch (e) { setErreur(errMsg(e)); }
  }, []);
  useEffect(() => { chargerCarriers(); }, [chargerCarriers]);

  const generer = async (code = null) => {
    setErreur(''); setChargement(code ? 'Récupération chez EffiTrace…' : 'Récupération de tous les transporteurs…');
    try {
      const r = await api.sync(code, 3);
      setCarriers(await api.carriers());
      if (code) {
        const g = (r.carriers || []).find((x) => String(x.carrierCode) === String(code));
        ouvrir(g || null, code);
      }
    } catch (e) { setErreur(errMsg(e)); } finally { setChargement(''); }
  };

  const ouvrir = async (groupe, code) => {
    setErreur('');
    let g = groupe;
    if (!g) {
      const liste = await api.pending(code);
      g = liste.find((x) => String(x.carrierCode) === String(code)) || { carrierCode: code, carrierName: '—', items: [] };
    }
    setCarrier(g);
    setItems(g.items || []);
    setCoches(new Set((g.items || []).map((b) => b.id)));   // tout coché par défaut
    setEtape(2);
  };

  // Le camion se presente : on ouvre la fiche du transporteur, qui interroge
  // EffiTrace en direct (ce qui peut sortir) et lit ce qui attend une signature.
  // bordereaux à signer d'UN transporteur, depuis Zeus (sans EffiTrace) ; une erreur de lecture reste une erreur (≠ « aucun »)
  // sans les bordereaux dont l'annulation est envoyée et pas confirmée (« en vérification ») : pas signables
  const aSignerDe = async (code) => ((await api.pending(code)).find((x) => String(x.carrierCode) === String(code))?.items || []).filter((b) => !b.annuleEtat);
  // erreur côté EffiTrace / serveur (robot, injoignable, délai, 5xx) — pas un refus de droit (403) ni « introuvable » (404)
  const erreurEffitrace = (e) => { const s = e?.response?.status; return !e?.response || s === 409 || s >= 500 || e?.code === 'ECONNABORTED'; };

  const ouvrirFiche = async (c) => {
    setErreur(''); setInfo(''); setChargement(`Interrogation d’EffiTrace — ${c.name}…`);
    try { setFiche(await api.carrier(c.code)); setEtape('fiche'); }
    catch (e) {
      // EffiTrace illisible (robot du convoyeur, panne, délai) : la fiche s'ouvre quand même avec ce que Zeus connaît
      // (bordereaux à signer, génération automatique) — jamais un transporteur inaccessible. Une autre erreur reste une erreur.
      if (!erreurEffitrace(e)) { setErreur(errMsg(e)); setChargement(''); return; }
      try {
        const aSigner = await aSignerDe(c.code);
        setFiche({ carrierCode: c.code, carrierName: c.name, horsLigne: `EffiTrace n’a pas pu être lu (${errMsg(e)})`,
          umEnAttente: 0, umControlees: 0, peutCreer: false, clients: [], aSigner });
        setEtape('fiche');
      } catch (e2) { setErreur(`${errMsg(e)} — et la liste à signer n’a pas pu être lue (${errMsg(e2)}).`); }
    } finally { setChargement(''); }
  };

  // SEULE action qui ecrit dans EffiTrace : creer le bordereau, puis le
  // rapatrier immediatement pour qu'il soit signable sans attendre.
  const genererBordereau = async () => {
    if (!fiche) return;
    setErreur(''); setInfo(''); setChargement('Création du bordereau dans EffiTrace, puis impression…');
    const code = fiche.carrierCode;
    const jeton = ++suivi.current;
    let x = null;   // résultat de l'exécution suivie (lu aussi après le bloc)
    const encore = () => suivi.current === jeton;
    try {
      const { depuis } = await api.autoLancer({ carrierCode: code, carrierName: fiche.carrierName, fiche: true,
        colis: fiche.umEnAttente || 0, controles: fiche.umControlees || 0 });
      for (let i = 0; i < 300; i++) {   // au plus 15 min (une attente d'EffiTrace) ; 3 s entre deux regards
        await new Promise((ok) => setTimeout(ok, 3000));
        if (!encore()) return;   // fiche quittée : on s'arrête là (le journal dira le résultat)
        x = await api.autoExecution(code, depuis).catch(() => null);
        if (x?.aucune && ((x.fileVide && i >= 1) || i >= 20)) break;   // pas d'exécution après 1 min (ou file vide) : elle n'a pas démarré
        if (x && !x.aucune && !x.enFile) break;
      }
      if (!encore()) return;
      if (x?.aucune) setErreur('La génération n’a pas démarré (erreur du serveur) : regardez le journal de la liste avant de réessayer.');
      else if (!x || x.enFile) setErreur('La génération continue en arrière-plan : son résultat sera dans le journal de la liste.');
      else if (x.statut === 'ERREUR') setErreur(x.message || 'La génération n’a pas abouti : voir le journal.');
      else if (x.statut === 'RIEN') setErreur(`Aucun bordereau créé : ${x.message || 'rien à générer'}`);   // demandé, rien fait : jamais en vert
      else if (x.message) setErreur(`Bordereau créé (${x.bordereaux}, ${x.colis} colis), mais : ${x.message}`);   // ex. pas imprimé
      else if (x.impression && !x.impression.disponible) {
        setErreur(`${x.bordereaux} bordereau${x.bordereaux > 1 ? 'x' : ''} créé${x.bordereaux > 1 ? 's' : ''} (${x.colis} colis), prêt à être signé, mais `
          + (x.impression.configuree === false
            ? 'l’impression automatique n’est pas configurée : imprimez-le à la main.'
            : 'l’agent d’impression du dépôt ne répond pas : l’impression attend (au plus 1 h) — sinon imprimez-le à la main.'));
      }
      else setInfo(`${x.bordereaux} bordereau${x.bordereaux > 1 ? 'x' : ''} créé${x.bordereaux > 1 ? 's' : ''} (${x.colis} colis), envoyé${x.bordereaux > 1 ? 's' : ''} à la Konica — prêt${x.bordereaux > 1 ? 's' : ''} à être signé${x.bordereaux > 1 ? 's' : ''}.`);
    } catch (e) { if (encore()) { setErreur(errMsg(e)); setChargement(''); } return; }
    if (!encore()) return;
    // génération encore en cours (suivi arrêté) : ne PAS relire EffiTrace — une connexion couperait la sienne
    if (!x || x.aucune || x.enFile) { setChargement(''); return; }
    // relire la fiche APRÈS avoir dit le résultat : un refus d'EffiTrace ici ne l'efface pas
    try { const f2 = await api.carrier(code); if (encore()) setFiche(f2); } catch (e) {
      // chiffres d'avant la génération : on ne les montre plus (ni le bouton qui irait avec) ; la liste à signer est relue à part
      const quoi = erreurEffitrace(e) ? `EffiTrace n’a pas pu être relu (${errMsg(e)})` : `La fiche n’a pas pu être relue (${errMsg(e)})`;
      let aSigner = null;
      try { aSigner = await aSignerDe(code); } catch { /* dit ci-dessous */ }
      if (encore()) {
        setFiche((f) => f && ({ ...f, aSigner: aSigner || f.aSigner,
          horsLigne: aSigner ? quoi : `${quoi} ; la liste à signer non plus — elle peut être incomplète, revenez à la liste` }));
      }
    }
    if (!encore()) return;   // (la liste des transporteurs se relit au retour)
    setChargement('');
  };

  // « Actualiser depuis EffiTrace » doit RAPATRIER, pas seulement relire.
  // ⚠️ MESURE 02/09 : ce bouton n'appelait que ouvrirFiche() — une lecture.
  // La seule fonction qui importe (api.sync) vivait dans generer(), qui n'etait
  // appelee nulle part : du code mort. Mesure sur place — les bordereaux 945
  // (Silviu) et 947 (Vincent), crees DANS EffiTrace, n'arrivaient jamais dans
  // Zeus et ne pouvaient donc pas etre signes. Seuls ceux crees DEPUIS Zeus
  // remontaient, car createBordereau() appelle la synchro lui-meme.
  const rafraichirFiche = async () => {
    if (!fiche) return;
    setErreur('');
    setChargement(`Récupération chez EffiTrace — ${fiche.carrierName}…`);
    try {
      await api.sync(fiche.carrierCode, 3);
    } catch (e) {
      setErreur(errMsg(e)); setChargement(''); return;
    }
    setChargement('');
    await ouvrirFiche({ code: fiche.carrierCode, name: fiche.carrierName });
  };

  // depuis la fiche : passer a la selection des bordereaux a signer
  useEffect(() => { if (etape === 1) { setErreur(''); setInfo(''); } }, [etape]);   // retour à la liste : les messages d'une fiche ne restent pas

  const versSignature = () => {
    const items = fiche?.aSigner || [];
    setCarrier({ carrierCode: fiche.carrierCode, carrierName: fiche.carrierName, items });
    setItems(items);
    setCoches(new Set(items.map((b) => b.id)));
    setEtape(2);
  };

  const bascule = (id) => setCoches((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const selection = useMemo(() => items.filter((b) => coches.has(b.id)), [items, coches]);
  const totaux = useMemo(() => ({
    b: selection.length,
    colis: selection.reduce((s, x) => s + (x.parcels || 0), 0),
    poids: selection.reduce((s, x) => s + (x.weightKg || 0), 0),
  }), [selection]);

  // ------------------------------------------------------------------
  // Pavé de signature
  // ------------------------------------------------------------------
  // Le tracé est conservé comme des POINTS (en coordonnées 0..1), pas comme
  // des pixels : tourner le téléphone déclenche un redimensionnement, et
  // redimensionner un canvas l'efface. En gardant les points, on redessine à
  // la nouvelle taille et la signature survit au passage portrait ↔ paysage.
  const padRef = useRef(null);
  const [signe, setSigne] = useState(false);
  const traces = useRef([]);          // [[{x,y}, …], …] en 0..1
  const enCours = useRef(null);

  const dessiner = useCallback(() => {
    const cv = padRef.current;
    if (!cv) return;
    const r = cv.getBoundingClientRect();
    const ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, cv.width, cv.height);
    ctx.lineWidth = 2.4; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0F1B5A';
    for (const trait of traces.current) {
      if (trait.length < 2) continue;
      ctx.beginPath();
      ctx.moveTo(trait[0].x * r.width, trait[0].y * r.height);
      for (const p of trait.slice(1)) ctx.lineTo(p.x * r.width, p.y * r.height);
      ctx.stroke();
    }
  }, []);

  const dimensionnerPad = useCallback(() => {
    const cv = padRef.current;
    if (!cv) return;
    const r = cv.getBoundingClientRect();
    if (!r.width || !r.height) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    cv.width = Math.round(r.width * dpr);
    cv.height = Math.round(r.height * dpr);
    cv.getContext('2d').setTransform(dpr, 0, 0, dpr, 0, 0);
    dessiner();                       // on NE vide PAS : on redessine
  }, [dessiner]);

  useEffect(() => {
    if (etape !== 3) return undefined;
    dimensionnerPad();
    window.addEventListener('resize', dimensionnerPad);
    window.addEventListener('orientationchange', dimensionnerPad);
    return () => {
      window.removeEventListener('resize', dimensionnerPad);
      window.removeEventListener('orientationchange', dimensionnerPad);
    };
  }, [etape, dimensionnerPad]);

  const pos = (e) => {
    const r = padRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: (p.clientX - r.left) / r.width, y: (p.clientY - r.top) / r.height };
  };
  const debut = (e) => {
    e.preventDefault();
    enCours.current = [pos(e)];
    traces.current.push(enCours.current);
  };
  const bouge = (e) => {
    if (!enCours.current) return;
    e.preventDefault();
    enCours.current.push(pos(e));
    dessiner();
    if (!signe) setSigne(true);
  };
  const fin = () => { enCours.current = null; };

  const effacerPad = useCallback(() => {
    traces.current = [];
    enCours.current = null;
    dessiner();
    setSigne(false);
  }, [dessiner]);

  // Le cadre EffiTrace est large et bas (157 x 36 pt). Exporter le pavé entier
  // donnerait un tracé minuscule perdu dans du vide : on recadre sur ce qui a
  // réellement été dessiné avant d'envoyer.
  const exporterSignature = () => {
    const cv = padRef.current;
    const ctx = cv.getContext('2d');
    const img = ctx.getImageData(0, 0, cv.width, cv.height);
    let x0 = cv.width, y0 = cv.height, x1 = -1, y1 = -1;
    for (let y = 0; y < cv.height; y += 1) {
      for (let x = 0; x < cv.width; x += 1) {
        if (img.data[(y * cv.width + x) * 4 + 3] > 8) {
          if (x < x0) x0 = x; if (x > x1) x1 = x;
          if (y < y0) y0 = y; if (y > y1) y1 = y;
        }
      }
    }
    if (x1 < 0) return null;
    const m = 6;
    x0 = Math.max(0, x0 - m); y0 = Math.max(0, y0 - m);
    x1 = Math.min(cv.width - 1, x1 + m); y1 = Math.min(cv.height - 1, y1 + m);
    const out = document.createElement('canvas');
    out.width = x1 - x0 + 1; out.height = y1 - y0 + 1;
    out.getContext('2d').drawImage(cv, x0, y0, out.width, out.height, 0, 0, out.width, out.height);
    return out.toDataURL('image/png');
  };

  // ------------------------------------------------------------------
  // Enregistrement
  // ------------------------------------------------------------------
  const enregistrer = async () => {
    setErreur('');
    if (!chauffeur.trim()) { setErreur('Le nom du chauffeur est obligatoire.'); return; }
    if (!signe) { setErreur('La signature du chauffeur est requise.'); return; }
    if (!selection.length) { setErreur('Aucun bordereau sélectionné.'); return; }
    setChargement('Téléchargement des bordereaux et signature…');
    try {
      const r = await api.handover({
        bordereauIds: selection.map((b) => b.id),
        driverName: chauffeur.trim(),
        reserves: reserves.trim() || null,
        signatureDataUrl: exporterSignature(),
      });
      setPreuve({
        ...r, chauffeur: chauffeur.trim(), quand: new Date(),
        transporteur: carrier?.carrierName, bordereaux: selection.length,
        colis: totaux.colis, poids: totaux.poids,
      });
      setEtape(4);
    } catch (e) { setErreur(errMsg(e)); } finally { setChargement(''); }
  };

  const recommencer = async () => {
    setChauffeur(''); setReserves(''); setPreuve(null); effacerPad();
    setCarrier(null); setItems([]); setCoches(new Set()); setFiche(null); setEtape(1);
    await chargerCarriers();
  };

  // ==================================================================
  // Rendu
  // ==================================================================
  return (
    <div style={{ background: C.paper, minHeight: '100vh' }}>
      {/* la liste des transporteurs a deux colonnes sur PC (tableau + journal) ; fiche, signature et archives restent au format PDA */}
      <div style={onglet === 'remise' && etape === 1 ? { ...S.wrap, maxWidth: 1300, padding: '0 12px 90px' } : S.wrap}>
        <Breadcrumbs items={[{ label: 'Remise transporteur' }]} />

        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${archivesVisibles ? 2 : 1}, 1fr)`, background: C.surface, border: `1px solid ${C.line}`, borderRadius: 10, overflow: 'hidden', margin: '0 auto 12px', maxWidth: 560 }}>
          {[['remise', '📦 Remise'], ['archives', '🗂️ Archives']].filter(([id]) => id === 'remise' || archivesVisibles).map(([id, txt]) => (
            <button key={id} type="button" onClick={() => !chargement && setOnglet(id)}
              style={{ padding: '11px 2px', border: 0, cursor: 'pointer', fontWeight: 600, fontSize: 13.5, whiteSpace: 'nowrap',
                background: onglet === id ? C.accentSoft : C.surface, color: onglet === id ? C.accent : C.ink3 }}>
              {txt}
            </button>
          ))}
        </div>

        {erreur && <div style={{ ...S.err, marginBottom: 12 }}>{erreur}</div>}
        {info && (
          <div style={{ fontSize: 13, color: C.ok, background: C.okSoft, padding: '9px 12px',
            borderRadius: 8, marginBottom: 12 }}>{info}</div>
        )}
        {chargement && (
          <div style={{ ...S.card, marginBottom: 12, display: 'flex', gap: 10, alignItems: 'center', color: C.ink2 }}>
            <span style={{ fontSize: 18 }}>⏳</span>{chargement}
          </div>
        )}

        {onglet === 'remise' && etape === 1 && (
          <ListeRemise carriers={carriers} onChoisir={ouvrirFiche} onRafraichir={chargerCarriers} occupe={!!chargement} peutLancer={peutSigner} peutRegler={peutRegler} peutSansSignature={peutSansSignature} />
        )}
        {onglet === 'remise' && etape === 'fiche' && (
          <FicheTransporteur fiche={fiche} peutSigner={peutSigner} peutRegler={peutRegler} occupe={!!chargement}
            onRetour={() => { setFiche(null); setEtape(1); chargerCarriers(); }}
            onGenerer={genererBordereau}
            onSigner={versSignature} onActualiser={rafraichirFiche}
            onApresVerdict={async (texte, ton) => {
              // verdict = Zeus seul : « à signer » relu dans Zeus, sans nouvelle session EffiTrace
              try { const aSigner = await aSignerDe(fiche.carrierCode); setFiche((f) => (f ? { ...f, aSigner } : f)); } catch { /* la fiche garde sa liste */ }
              if (ton === 'ok') setInfo(texte); else setErreur(texte);
            }}
            onApresAnnulation={async (texte, ton) => {
              // délai dépassé : Zeus travaille peut-être encore dans EffiTrace — relire la fiche ouvrirait une 2e session et le couperait
              if (/Délai dépassé/.test(texte)) { setErreur(`${texte} Rouvrez la fiche dans 3 minutes.`); return; }
              await ouvrirFiche({ code: fiche.carrierCode, name: fiche.carrierName });
              if (ton === 'ok') setInfo(texte); else setErreur(texte);
            }} />
        )}
        {onglet === 'remise' && etape === 2 && (
          <Liste carrier={carrier} items={items} coches={coches} onBascule={bascule} totaux={totaux}
            peutSigner={peutSigner} onRetour={() => setEtape(1)} onSigner={() => setEtape(3)} />
        )}
        {onglet === 'remise' && etape === 3 && (
          <Signature carrier={carrier} totaux={totaux} selection={selection}
            chauffeur={chauffeur} setChauffeur={setChauffeur} reserves={reserves} setReserves={setReserves}
            padRef={padRef} signe={signe} onEffacer={effacerPad} onDebut={debut} onBouge={bouge} onFin={fin}
            onRetour={() => setEtape(2)} onEnregistrer={enregistrer} occupe={!!chargement} />
        )}
        {onglet === 'remise' && etape === 4 && preuve && (
          <Confirmation preuve={preuve} operateur={user?.name} onTerminer={recommencer} />
        )}

        {onglet === 'archives' && <Archives carriers={carriers} />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// 1. Transporteurs — la liste est TOUJOURS là : ListeRemise (bordereaux/BordereauxAuto.jsx), le tableau des colis
//    d'EffiTrace, des bordereaux à signer et de la génération automatique (page unique, 28-10-2026).
// ---------------------------------------------------------------------

// ---------------------------------------------------------------------
// 1b. Fiche transporteur — ce qui peut sortir, et ce qui attend une signature
// ---------------------------------------------------------------------
function FicheTransporteur({ fiche, onRetour, onGenerer, onSigner, onActualiser, onApresAnnulation, onApresVerdict, occupe, peutSigner, peutRegler }) {
  const [annul, setAnnul] = useState(null);   // dialogue d'annulation (bordereau non signé ou remise signée)
  const [remises, setRemises] = useState([]);
  const [erreurRemises, setErreurRemises] = useState('');
  useEffect(() => {
    if (!fiche) return undefined;
    let vivant = true;
    setErreurRemises('');
    api.remisesDuJour(fiche.carrierCode).then((r) => { if (vivant) setRemises(r); })
      .catch((e) => { if (vivant) { setRemises([]); setErreurRemises((x) => `${x ? `${x} · ` : ''}Remises signées aujourd'hui illisibles : ${errMsg(e)}`); } });
    api.aControler(fiche.carrierCode).then((r) => { if (vivant) setAControler(r); })
      .catch((e) => { if (vivant) { setAControler([]); setErreurRemises((x) => `${x ? `${x} · ` : ''}Annulations à contrôler illisibles : ${errMsg(e)}`); } });
    return () => { vivant = false; };
  }, [fiche]);
  // annulations envoyées à EffiTrace et pas confirmées : l'opérateur regarde dans EffiTrace et tranche (Zeus seul, sans connexion)
  const [aControler, setAControler] = useState([]);
  const [verdict, setVerdict] = useState(null);   // { id, verdict } en attente de confirmation
  const [enVerdict, setEnVerdict] = useState(false);
  const trancher = async () => {
    setEnVerdict(true);
    try {
      const r = await api.verdictAnnulation(verdict.id, verdict.verdict);
      setAControler((l) => l.filter((b) => b.id !== verdict.id));
      api.remisesDuJour(fiche.carrierCode).then(setRemises).catch(() => {});
      setVerdict(null); onApresVerdict(r.message, 'ok');
    } catch (e) { setVerdict(null); onApresVerdict(errMsg(e), 'crit'); }
    finally { setEnVerdict(false); }
  };
  const [confirme, setConfirme] = useState(false);
  if (!fiche) return null;
  const { umEnAttente, umControlees, peutCreer, raison, clients, aSigner } = fiche;

  return (
    <>
      {/* pendant une génération on reste sur la fiche (le résultat et la liste à signer vont arriver) */}
      <Entete titre={fiche.carrierName} sous="Camion au quai" onRetour={occupe ? undefined : onRetour} />

      {/* 1. ce qui peut etre mis sur un bordereau maintenant */}
      <div style={{ ...S.card, marginTop: 12 }}>
        <div style={{ ...S.k, marginBottom: 8 }}>En attente de bordereau, chez EffiTrace</div>

        {fiche.horsLigne ? (
          <div style={{ fontSize: 13, color: C.ink2, background: C.surface2, padding: '10px 12px', borderRadius: 8 }}>
            {fiche.horsLigne} : les colis en attente ne sont pas connus, la génération attend.
            Les bordereaux déjà créés peuvent être signés ci-dessous.
          </div>
        ) : umEnAttente > 0 ? (
          <>
            <div style={{ display: 'flex', gap: 20, marginBottom: 8 }}>
              <div>
                <div style={S.k}>UM en attente</div>
                <span style={{ ...S.mono, fontSize: 22, fontWeight: 600 }}>{umEnAttente}</span>
              </div>
              <div>
                <div style={S.k}>Contrôlées</div>
                <span style={{ ...S.mono, fontSize: 22, fontWeight: 600,
                  color: umControlees ? C.ok : C.ink3 }}>{umControlees}</span>
              </div>
            </div>
            {clients?.length > 0 && (
              <div style={{ fontSize: 12.5, color: C.ink3, marginBottom: 10 }}>
                {clients.map((x) => x.nom).join(', ')}
              </div>
            )}
            {peutCreer && peutSigner ? (
              // Confirmation en deux temps : c'est la SEULE action du module qui
              // ecrit dans EffiTrace. On dit exactement ce qui va etre cree.
              !confirme ? (
                <button type="button" style={{ ...S.big, opacity: occupe ? 0.5 : 1 }}
                  onClick={() => setConfirme(true)} disabled={occupe}>
                  Générer + imprimer ({umControlees} UM)
                </button>
              ) : (
                <div style={{ background: C.surface2, borderRadius: 8, padding: 12 }}>
                  <div style={{ fontSize: 13, marginBottom: 10 }}>
                    Créer le bordereau dans <b>EffiTrace</b> pour{' '}
                    <b>{(clients || []).map((x) => x.nom).join(', ')}</b> — {umControlees} UM contrôlées.
                    <br />C’est une écriture réelle, comme si vous le faisiez à la main dans EffiTrace ; le bordereau part ensuite à la Konica.
                  </div>
                  <button type="button" style={{ ...S.big, opacity: occupe ? 0.5 : 1 }}
                    onClick={() => { setConfirme(false); onGenerer(); }} disabled={occupe}>
                    Oui, créer et imprimer
                  </button>
                  <button type="button" style={{ ...S.bigSec, marginTop: 9, padding: 11 }}
                    onClick={() => setConfirme(false)}>Annuler</button>
                </div>
              )
            ) : (
              <div style={{ fontSize: 13, color: C.ink2, background: C.surface2,
                padding: '10px 12px', borderRadius: 8 }}>
                {peutCreer ? 'Générer un bordereau demande le droit « Générer les bordereaux et faire signer le chauffeur ».' : raison}
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: 13, color: C.ink3 }}>
            Rien en attente de bordereau pour ce transporteur.
          </div>
        )}
      </div>

      {/* 2. ce qui attend une signature chez nous */}
      <div style={{ ...S.card, marginTop: 12, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '11px 13px', borderBottom: aSigner.length ? `1px solid ${C.line}` : 0 }}>
          <div style={S.k}>Bordereaux à faire signer</div>
        </div>
        {aSigner.length === 0 ? (
          <div style={{ ...S.empty, padding: '20px' }}>
            Aucun bordereau en attente de signature.
          </div>
        ) : (
          <>
            {aSigner.map((b) => (
              <div key={b.id} style={{ padding: '11px 14px', borderBottom: `1px solid ${C.line}`, display: 'flex', gap: 10, alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{b.clientName}</div>
                  <div style={{ fontSize: 12.5, color: C.ink3 }}>
                    Bordereau <b style={S.mono}>n° {b.numero}</b> · {b.parcels} colis · {kg(b.weightKg)}
                  </div>
                </div>
                {peutSigner && (
                  <button type="button" disabled={occupe}
                    onClick={() => setAnnul({ titre: `${fiche.carrierName} : annuler le bordereau n° ${b.numero} ?`,
                      items: [{ id: b.id, numero: b.numero, clientName: b.clientName, carrierName: fiche.carrierName, parcels: b.parcels }] })}
                    style={{ padding: '8px 11px', minHeight: 40, borderRadius: 8, border: '1.5px solid #E2B4AF', background: C.surface, color: C.crit, cursor: 'pointer', font: 'inherit' }}>
                    ✖ Annuler
                  </button>
                )}
              </div>
            ))}
            <div style={{ padding: 13 }}>
              <button type="button" style={{ ...S.big, opacity: peutSigner && !occupe ? 1 : 0.4 }}
                onClick={onSigner} disabled={!peutSigner || occupe} title={occupe ? 'Une génération est en cours : la liste à signer va changer' : ''}>
                Faire signer ({aSigner.length})
              </button>
            </div>
          </>
        )}
      </div>

      {peutSigner && (
        <button type="button" style={{ ...S.bigSec, marginTop: 12, opacity: occupe ? 0.5 : 1 }}
          onClick={onActualiser} disabled={occupe}>
          Actualiser depuis EffiTrace
        </button>
      )}

      {/* 3. remises signées aujourd'hui : annulables le jour même (la preuve signée reste dans l'archive) */}
      {erreurRemises && <div style={{ ...S.card, marginTop: 12, color: C.crit, fontSize: 13 }}>{erreurRemises}</div>}
      {aControler.length > 0 && (
        <div style={{ ...S.card, marginTop: 12, padding: 0, overflow: 'hidden', borderColor: '#E9C98F' }}>
          <div style={{ padding: '11px 13px', borderBottom: `1px solid ${C.line}` }}>
            <div style={S.k}>Annulations à contrôler dans EffiTrace</div>
            <div style={{ fontSize: 12, color: C.ink3, marginTop: 4 }}>
              Zeus a envoyé l’annulation mais EffiTrace ne l’a pas confirmée. Regardez le bordereau dans EffiTrace (« Bordereau de remise »), puis dites ce que vous voyez — en attendant, il ne peut pas être signé. S’il y est « déclôturé » (date de clôture N/A) : « ↻ Relancer l’annulation », Zeus finit le geste.
            </div>
          </div>
          {aControler.map((b) => {
            const ici = verdict?.id === b.id;
            const bouton = (v, texte) => (
              <button type="button" disabled={occupe || enVerdict} onClick={() => setVerdict({ id: b.id, verdict: v })}
                style={{ padding: '8px 11px', minHeight: 40, borderRadius: 8, border: `1.5px solid ${C.lineStrong}`, background: C.surface, cursor: 'pointer', font: 'inherit' }}>
                {texte}
              </button>
            );
            return (
              <div key={b.id} style={{ padding: '11px 14px', borderBottom: `1px solid ${C.line}` }}>
                <div style={{ fontWeight: 600, fontSize: 14.5 }}>{b.clientName} <span style={S.mono}>n° {b.numero}</span> · {b.parcels} colis{b.status === 'REMIS' ? ' · signé' : ''}</div>
                <div style={{ fontSize: 12.5, color: C.ink3 }}>
                  {b.annuleEtat === 'DECLOTURE_ENVOYEE' ? 'Déclôture envoyée' : 'Annulation de la sortie envoyée'} le {dt(b.annuleLe)} par {b.annuleParNom || '—'} · « {b.annuleRaison || '—'} »
                </div>
                {peutSigner && !ici && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                    {bouton('annule', b.annuleEtat === 'DECLOTURE_ENVOYEE' ? '✔ Il n’est plus dans EffiTrace' : '✔ Sa sortie est annulée dans EffiTrace')}
                    {bouton('present', '↩ Il y est encore, clôturé (rien n’a changé)')}
                    {b.status === 'A_REMETTRE' && (
                      <button type="button" disabled={occupe || enVerdict}
                        onClick={() => setAnnul({ titre: `${fiche.carrierName} : relancer l’annulation du n° ${b.numero} ?`,
                          items: [{ id: b.id, numero: b.numero, clientName: b.clientName, carrierName: fiche.carrierName, parcels: b.parcels }] })}
                        style={{ padding: '8px 11px', minHeight: 40, borderRadius: 8, border: '1.5px solid #E2B4AF', background: C.surface, color: C.crit, cursor: 'pointer', font: 'inherit' }}>
                        ↻ Relancer l’annulation
                      </button>
                    )}
                  </div>
                )}
                {ici && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
                    <span style={{ fontSize: 13 }}>
                      {verdict.verdict === 'annule' ? 'Confirmer : marquer ce bordereau annulé (colis à régénérer) ?' : 'Confirmer : il redevient normal, rien n’est annulé ?'}
                    </span>
                    <button type="button" disabled={enVerdict} onClick={trancher}
                      style={{ padding: '8px 12px', minHeight: 40, borderRadius: 8, border: 'none', background: C.ink, color: '#fff', cursor: 'pointer', font: 'inherit' }}>
                      {enVerdict ? '⏳…' : 'Oui, confirmer'}
                    </button>
                    <button type="button" disabled={enVerdict} onClick={() => setVerdict(null)}
                      style={{ padding: '8px 12px', minHeight: 40, borderRadius: 8, border: `1.5px solid ${C.lineStrong}`, background: C.surface, cursor: 'pointer', font: 'inherit' }}>
                      Non
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {remises.length > 0 && (
        <div style={{ ...S.card, marginTop: 12, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '11px 13px', borderBottom: `1px solid ${C.line}` }}>
            <div style={S.k}>Remises signées aujourd'hui</div>
          </div>
          {remises.map((h) => (
            <div key={h.id} style={{ padding: '11px 14px', borderBottom: `1px solid ${C.line}`, display: 'flex', gap: 10, alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14.5 }}>{h.driverName} · {dt(h.signedAt)}{h.commencee ? ' · annulation commencée, à finir' : ''}</div>
                <div style={{ fontSize: 12.5, color: C.ink3 }}>{h.bordereaux.map((b) => `n° ${b.numero} ${b.clientName}`).join(', ')} · {h.parcels} colis</div>
              </div>
              {peutSigner && (
                <button type="button" disabled={occupe}
                  onClick={() => setAnnul({ titre: `${fiche.carrierName} : annuler la remise signée de ${h.driverName} ?`, remise: h,
                    items: h.bordereaux.map((b) => ({ ...b, carrierName: fiche.carrierName })) })}
                  style={{ padding: '8px 11px', minHeight: 40, borderRadius: 8, border: '1.5px solid #E2B4AF', background: C.surface, color: C.crit, cursor: 'pointer', font: 'inherit' }}>
                  ✖ Annuler la remise
                </button>
              )}
            </div>
          ))}
          <div style={{ fontSize: 11.5, color: C.ink3, padding: '8px 14px' }}>Une remise signée ne s’annule que le jour même (une annulation commencée se finit dans les 7 jours). La preuve signée reste dans l’archive, marquée « annulée ».</div>
        </div>
      )}
      {annul && <AnnulerDialog {...annul} onFermer={() => setAnnul(null)} onFait={(texte, ton) => { setAnnul(null); onApresAnnulation(texte, ton); }} />}

      {/* 4. génération automatique de ce transporteur (réglable avec le droit « Paramétrer », sinon une phrase) */}
      <ReglagesAuto carrierCode={fiche.carrierCode} carrierName={fiche.carrierName} peutRegler={peutRegler} />
    </>
  );
}


// ---------------------------------------------------------------------
// 2. Liste des bordereaux
// ---------------------------------------------------------------------
function Liste({ carrier, items, coches, onBascule, totaux, peutSigner, onRetour, onSigner }) {
  return (
    <>
      <Entete titre={carrier?.carrierName || '—'}
        sous={`${items.length} bordereau${items.length > 1 ? 'x' : ''} · tout est coché`} onRetour={onRetour} />
      <div style={{ ...S.card, padding: 0, overflow: 'hidden', margin: '12px 0' }}>
        {items.length === 0 && <div style={S.empty}>Aucun bordereau pour ce transporteur.</div>}
        {items.map((b) => {
          const on = coches.has(b.id);
          return (
            <button key={b.id} type="button" onClick={() => onBascule(b.id)}
              style={{ display: 'grid', gridTemplateColumns: '26px 1fr', gap: 12, background: C.surface, border: 0,
                borderBottom: `1px solid ${C.line}`, padding: '13px 14px', alignItems: 'start', width: '100%',
                textAlign: 'left', cursor: 'pointer', opacity: on ? 1 : 0.42 }}>
              <span style={{ width: 24, height: 24, borderRadius: 6, border: `2px solid ${on ? C.accent : C.lineStrong}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15,
                background: on ? C.accent : 'transparent', color: on ? C.accentInk : 'transparent', marginTop: 1 }}>✓</span>
              <span>
                <span style={{ fontWeight: 600, fontSize: 15.5, display: 'block' }}>{b.clientName}</span>
                <span style={{ fontSize: 12.5, color: C.ink3, display: 'block', marginTop: 2 }}>
                  Bordereau <b style={S.mono}>n° {b.numero}</b> · {b.parcels} colis · {kg(b.weightKg)}
                </span>
                <span style={{ fontSize: 12.5, color: C.ink3 }}>{jour(b.eftCreatedAt)}</span>
              </span>
            </button>
          );
        })}
      </div>
      <div style={{ ...S.card }}>
        <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
          <div style={{ flex: 1 }}><div style={S.k}>Bordereaux</div><div style={S.v}>{totaux.b}</div></div>
          <div style={{ flex: 1 }}><div style={S.k}>Colis</div><div style={S.v}>{totaux.colis}</div></div>
          <div style={{ flex: 1 }}><div style={S.k}>Poids</div><div style={S.v}>{totaux.poids.toFixed(1)}</div></div>
        </div>
        <button type="button" style={{ ...S.big, opacity: totaux.b && peutSigner ? 1 : 0.4 }}
          disabled={!totaux.b || !peutSigner} onClick={onSigner}>
          Signer la remise
        </button>
        {!peutSigner && (
          <p style={{ fontSize: 12, color: C.ink3, marginTop: 8, textAlign: 'center' }}>
            Consultation seule — la permission « signer » est nécessaire.
          </p>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------
// 3. Signature
// ---------------------------------------------------------------------
function Signature({ carrier, totaux, selection, chauffeur, setChauffeur, reserves, setReserves,
  padRef, signe, onEffacer, onDebut, onBouge, onFin, onRetour, onEnregistrer, occupe }) {
  return (
    <>
      <Entete titre="Signature du chauffeur"
        sous={`${carrier?.carrierName} · ${totaux.b} bordereau${totaux.b > 1 ? 'x' : ''}`} onRetour={onRetour} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
        <div style={{ background: C.accentSoft, color: C.accent, fontSize: 12.5, fontWeight: 600,
          padding: '9px 12px', borderRadius: 8 }}>
          📲 Passez l’appareil au chauffeur
        </div>

        <div style={S.card}>
          <div style={{ ...S.k, marginBottom: 8 }}>Prise en charge</div>
          <div style={{ display: 'flex', gap: 20 }}>
            <div><div style={S.k}>Colis</div><span style={{ ...S.mono, fontSize: 22, fontWeight: 600 }}>{totaux.colis}</span></div>
            <div><div style={S.k}>Poids brut</div><span style={{ ...S.mono, fontSize: 22, fontWeight: 600 }}>{totaux.poids.toFixed(1)}</span></div>
          </div>
          <ul style={{ listStyle: 'none', margin: '9px 0 0', padding: '9px 0 0', borderTop: `1px solid ${C.line}`,
            display: 'flex', flexDirection: 'column', gap: 5 }}>
            {selection.map((b) => (
              <li key={b.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, gap: 10 }}>
                <span>{b.clientName}</span>
                <b style={S.mono}>n° {b.numero} · {b.parcels}</b>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <label style={S.lbl} htmlFor="drv">Nom du chauffeur</label>
          <input id="drv" type="text" style={S.input} value={chauffeur} autoComplete="off"
            onChange={(e) => setChauffeur(e.target.value)} placeholder="Dupont Michel" />
        </div>
        <div>
          <label style={S.lbl} htmlFor="res">Réserves</label>
          <textarea id="res" style={{ ...S.input, minHeight: 54, resize: 'none' }} value={reserves}
            onChange={(e) => setReserves(e.target.value)} placeholder="Aucune réserve" />
        </div>

        <div>
          <span style={S.lbl}>Signature</span>
          <div style={{ position: 'relative', border: `2px ${signe ? 'solid' : 'dashed'} ${signe ? C.accent : C.lineStrong}`,
            borderRadius: 10, background: C.surface }}>
            {/* fond blanc EXPLICITE : sans lui le navigateur peut peindre la zone en
                noir. En CSS et pas dans le bitmap — le PNG exporte reste transparent,
                sinon un rectangle blanc recouvrirait le cadre imprime du bordereau. */}
            <canvas ref={padRef} style={{ display: 'block', width: '100%', height: 190,
              touchAction: 'none', background: '#FFFFFF', borderRadius: 8 }}
              onMouseDown={onDebut} onMouseMove={onBouge} onMouseUp={onFin} onMouseLeave={onFin}
              onTouchStart={onDebut} onTouchMove={onBouge} onTouchEnd={onFin} />
            <div style={{ position: 'absolute', left: 20, right: 20, bottom: 38,
              borderBottom: `1px solid ${C.lineStrong}`, pointerEvents: 'none' }} />
            {!signe && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 5, pointerEvents: 'none', color: C.ink3, fontSize: 13.5 }}>
                <span style={{ fontSize: 24, opacity: 0.5 }}>✍</span>Signez avec le doigt
              </div>
            )}
          </div>
          <button type="button" style={{ ...S.bigSec, marginTop: 9, padding: 11 }} onClick={onEffacer}>Effacer</button>
        </div>

        <button type="button" style={{ ...S.big, opacity: occupe ? 0.5 : 1 }} onClick={onEnregistrer} disabled={occupe}>
          Enregistrer
        </button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------
// 4. Confirmation
// ---------------------------------------------------------------------
function Confirmation({ preuve, operateur, onTerminer }) {
  // 04/09 — cet ecran affichait un ✓ vert meme quand la sortie EffiTrace avait echoue.
  // La valeur arrivait pourtant jusqu'ici (setPreuve recopie toute la reponse) : personne
  // ne la lisait. ⚠️ sortirBordereaux echoue de QUATRE facons et une seule pose `erreur` :
  // aucun id, rien a sortir chez EffiTrace, plus « non sortis », ou exception HTTP.
  // Le seul vrai succes est donc `confirme === true`, pas l'absence d'erreur.
  const sortie = preuve.sortie;
  const sortieOk = sortie?.confirme === true;
  return (
    <div style={{ ...S.card, textAlign: 'center' }}>
      <div style={{ width: 56, height: 56, borderRadius: '50%',
        background: sortieOk ? C.okSoft : C.critSoft, color: sortieOk ? C.ok : C.crit, fontSize: 29,
        fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 13px' }}>
        {sortieOk ? '✓' : '!'}
      </div>
      <div style={{ fontSize: 17, fontWeight: 600 }}>
        {preuve.bordereaux} bordereau{preuve.bordereaux > 1 ? 'x' : ''} remis à {preuve.transporteur}
      </div>
      <div style={{ color: C.ink3, fontSize: 13.5, marginTop: 3 }}>
        {preuve.pages} page{preuve.pages > 1 ? 's' : ''} signée{preuve.pages > 1 ? 's' : ''} · {preuve.colis} colis · {kg(preuve.poids)}
      </div>

      {sortieOk ? (
        <div style={{ fontSize: 13, color: C.ok, background: C.okSoft, padding: '9px 12px',
          borderRadius: 8, marginTop: 12, textAlign: 'left' }}>
          Sortie confirmee chez EffiTrace : {sortie.sortis} bordereau{sortie.sortis > 1 ? 'x' : ''}
          {sortie.ignores ? ` \u00b7 ${sortie.ignores} deja sorti${sortie.ignores > 1 ? 's' : ''}` : ''}.
        </div>
      ) : (
        <div style={{ ...S.err, marginTop: 12, textAlign: 'left' }}>
          <b>La signature est enregistree, mais la sortie chez EffiTrace n\u2019est pas confirmee.</b>
          <div style={{ marginTop: 5 }}>
            {sortie?.erreur || sortie?.raison
              || (sortie ? 'EffiTrace n\u2019a pas confirme la sortie.' : 'Aucune reponse d\u2019EffiTrace n\u2019a ete enregistree.')}
            {sortie?.nonSortis?.length ? ` Restent non sortis : ${sortie.nonSortis.join(', ')}.` : ''}
          </div>
          <div style={{ marginTop: 5, color: C.ink2 }}>
            La marchandise est partie avec le chauffeur : sortez ces bordereaux dans EffiTrace,
            sinon ils resteront comptes comme non sortis.
          </div>
        </div>
      )}

      <div style={{ ...S.card, textAlign: 'left', marginTop: 16 }}>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {[['Chauffeur', preuve.chauffeur], ['Remis par', operateur || '—'],
            ['Horodatage', dt(preuve.quand)], ['Empreinte', preuve.listHash]].map(([k, v]) => (
              <li key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13,
                padding: '4px 0', borderBottom: `1px dotted ${C.line}` }}>
                <span>{k}</span><b style={k === 'Horodatage' || k === 'Empreinte' ? S.mono : undefined}>{v}</b>
              </li>
          ))}
        </ul>
      </div>
      <p style={{ fontSize: 12, color: C.ink3, marginTop: 10, textAlign: 'left' }}>
        Signature déposée dans le cadre prévu par EffiTrace, sur <b>chaque page</b>.
        Le PDF d’origine est conservé intact à côté du signé.
      </p>
      <Impression handoverId={preuve.handoverId} onAnnule={onTerminer} />
      <button type="button" style={{ ...S.bigSec, marginTop: 9 }} onClick={onTerminer}>Terminer</button>
    </div>
  );
}

// ---------------------------------------------------------------------
// Impression — commune à la confirmation et à l'archive
// ---------------------------------------------------------------------
// La preuve tient sur une feuille : c'est le défaut. L'assemblage complet
// annonce son nombre de pages avant d'ouvrir quoi que ce soit — 83 pages
// sur la première remise réelle, on n'imprime pas ça par mégarde.
function Impression({ handoverId, onAnnule }) {
  const [info, setInfo] = useState(null);
  const [confirmeTout, setConfirmeTout] = useState(false);
  const [msg, setMsg] = useState('');
  const [annuler, setAnnuler] = useState(false);
  const [raison, setRaison] = useState('');
  const [erreurAnnul, setErreurAnnul] = useState('');

  useEffect(() => {
    let vivant = true;
    api.handover1(handoverId).then((r) => { if (vivant) setInfo(r); }).catch(() => {});
    return () => { vivant = false; };
  }, [handoverId]);

  const imprimer = async (mode) => {
    setMsg('');
    try {
      const r = await api.print(handoverId, mode);
      setMsg(`Envoyé à l’imprimante du dépôt (${r.pages} page${r.pages > 1 ? 's' : ''}).`);
    } catch (e) { setMsg(errMsg(e)); }
  };

  const confirmerAnnulation = async () => {
    setErreurAnnul('');
    try {
      const r = await api.cancelHandover(handoverId, raison);
      setAnnuler(false); setRaison('');
      setInfo(await api.handover1(handoverId));
      setMsg(r.message);
      if (onAnnule) onAnnule();
    } catch (e) { setErreurAnnul(errMsg(e)); }
  };

  const pages = info?.pagesTotal ?? 0;
  return (
    <div style={{ marginTop: 13 }}>
      <a href={api.proofUrl(handoverId)} target="_blank" rel="noreferrer"
        style={{ ...S.big, textDecoration: 'none' }}>
        Imprimer la preuve (1 page)
      </a>

      {!confirmeTout ? (
        <button type="button" style={{ ...S.bigSec, marginTop: 9 }} onClick={() => setConfirmeTout(true)}>
          Imprimer tous les bordereaux{pages ? ` (${pages} pages)` : ''}
        </button>
      ) : (
        <div style={{ ...S.card, marginTop: 9 }}>
          <div style={{ fontSize: 13, marginBottom: 9 }}>
            {pages} page{pages > 1 ? 's' : ''} vont être ouvertes pour impression. Confirmer ?
          </div>
          <a href={api.allUrl(handoverId)} target="_blank" rel="noreferrer"
            style={{ ...S.big, textDecoration: 'none' }} onClick={() => setConfirmeTout(false)}>
            Oui, ouvrir les {pages} pages
          </a>
          <button type="button" style={{ ...S.bigSec, marginTop: 9 }} onClick={() => setConfirmeTout(false)}>
            Annuler
          </button>
        </div>
      )}

      {info?.printReady && (
        <div style={{ display: 'flex', gap: 9, marginTop: 9 }}>
          <button type="button" style={{ ...S.bigSec, flex: 1 }} onClick={() => imprimer('proof')}>
            Envoyer la preuve à la Konica
          </button>
          <button type="button" style={{ ...S.bigSec, flex: 1 }} onClick={() => imprimer('all')}>
            Envoyer les {pages} pages
          </button>
        </div>
      )}
      {msg && <p style={{ fontSize: 12.5, color: C.ink2, marginTop: 8 }}>{msg}</p>}

      {/* Annulation — le meme jour seulement, et jamais sans EffiTrace.
          Rien n'est efface : la remise reste en archive, marquee annulee. */}
      {info?.cancelledAt ? (
        <div style={{ ...S.card, marginTop: 13, background: C.critSoft, borderColor: C.crit }}>
          <div style={{ fontWeight: 600, fontSize: 13.5, color: C.crit }}>Remise annulée</div>
          <div style={{ fontSize: 12.5, color: C.ink2, marginTop: 4 }}>
            Par {info.cancelledByName} le {dt(info.cancelledAt)}<br />
            Motif : {info.cancelReason}
          </div>
        </div>
      ) : !info?.raisonNonAnnulable ? (
        !annuler ? (
          <button type="button" style={{ ...S.bigSec, marginTop: 13, color: C.crit, borderColor: C.crit }}
            onClick={() => setAnnuler(true)}>
            Annuler cette remise
          </button>
        ) : (
          <div style={{ ...S.card, marginTop: 13 }}>
            <div style={{ fontSize: 13, marginBottom: 9 }}>
              La signature sera annulée <b>et la sortie annulée dans EffiTrace</b>. Les UM
              retourneront en attente : il faudra régénérer un bordereau.
              <br />Rien n’est effacé — la remise reste dans l’archive, marquée annulée.
            </div>
            <label style={S.lbl} htmlFor="raison">Raison de l’annulation</label>
            <input id="raison" type="text" style={S.input} value={raison} autoComplete="off"
              onChange={(e) => setRaison(e.target.value)} placeholder="Signature par erreur, chauffeur reparti sans la marchandise…" />
            {erreurAnnul && <div style={{ ...S.err, marginTop: 9 }}>{erreurAnnul}</div>}
            <button type="button" style={{ ...S.big, marginTop: 10, background: C.crit, opacity: raison.trim() ? 1 : 0.4 }}
              disabled={!raison.trim()} onClick={confirmerAnnulation}>
              Oui, annuler la remise
            </button>
            <button type="button" style={{ ...S.bigSec, marginTop: 9, padding: 11 }}
              onClick={() => { setAnnuler(false); setErreurAnnul(''); }}>Retour</button>
          </div>
        )
      ) : (
        <p style={{ fontSize: 12, color: C.ink3, marginTop: 12, textAlign: 'center' }}>
          {info.raisonNonAnnulable}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// 5. Archives
// ---------------------------------------------------------------------
function Archives({ carriers }) {
  const [q, setQ] = useState('');
  const [carrierCode, setCarrierCode] = useState('');
  const [periode, setPeriode] = useState('30');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [erreur, setErreur] = useState('');

  // Anti-contradiction : une date précise et une période rapide se contredisent.
  // Dès qu'une date est saisie, les pastilles de période sont neutralisées —
  // l'utilisateur ne peut pas demander « aujourd'hui » ET « le 12 juin ».
  const dateActive = !!(from || to);
  // dateFrom > dateTo : on normalise au lieu de renvoyer une liste vide inexplicable.
  const bornes = useMemo(() => {
    if (from && to && from > to) return { from: to, to: from, inverse: true };
    return { from, to, inverse: false };
  }, [from, to]);

  useEffect(() => {
    let vivant = true;
    const params = { q: q.trim() || undefined, carrierCode: carrierCode || undefined };
    if (dateActive) {
      if (bornes.from) params.from = bornes.from;
      if (bornes.to) params.to = bornes.to;
    } else {
      const p = PERIODES.find((x) => x.id === periode);
      if (p && p.jours !== null) {
        const d = new Date(); d.setDate(d.getDate() - p.jours);
        params.from = d.toISOString().slice(0, 10);
      }
    }
    const t = setTimeout(async () => {
      try {
        const r = await api.archive(params);
        if (vivant) { setRows(r); setErreur(''); }
      } catch (e) { if (vivant) setErreur(errMsg(e)); }
    }, 250);
    return () => { vivant = false; clearTimeout(t); };
  }, [q, carrierCode, periode, dateActive, bornes]);

  if (detail) return <Detail b={detail} onRetour={() => setDetail(null)} />;

  return (
    <>
      <div style={{ ...S.card, marginBottom: 12 }}>
        <input type="text" value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off"
          placeholder="Client, n° de bordereau, chauffeur…"
          style={{ ...S.input, background: C.surface2, fontSize: 15, padding: '11px 12px' }} />

        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '9px 0 0' }}>
          <button type="button" style={S.chip(!carrierCode)} onClick={() => setCarrierCode('')}>Tous</button>
          {carriers.map((c) => (
            <button key={c.code} type="button" style={S.chip(carrierCode === c.code)}
              onClick={() => setCarrierCode(carrierCode === c.code ? '' : c.code)}>
              <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
                marginRight: 5, verticalAlign: 'middle', background: teinte(c.name) }} />
              {c.name}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingTop: 9,
          opacity: dateActive ? 0.35 : 1, pointerEvents: dateActive ? 'none' : 'auto' }}>
          {PERIODES.map((p) => (
            <button key={p.id} type="button" style={S.chip(periode === p.id)} onClick={() => setPeriode(p.id)}>
              {p.label}
            </button>
          ))}
        </div>

        <div style={{ ...S.k, padding: '11px 0 5px' }}>Ou une date précise</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 7, alignItems: 'center' }}>
          <input type="date" aria-label="Date de début" value={from} onChange={(e) => setFrom(e.target.value)}
            style={{ ...S.input, ...S.mono, fontSize: 13.5, padding: '9px 10px', minHeight: 40,
              background: from ? C.accentSoft : C.surface2, borderColor: from ? C.accent : C.lineStrong }} />
          <input type="date" aria-label="Date de fin (facultatif)" value={to} onChange={(e) => setTo(e.target.value)}
            style={{ ...S.input, ...S.mono, fontSize: 13.5, padding: '9px 10px', minHeight: 40,
              background: to ? C.accentSoft : C.surface2, borderColor: to ? C.accent : C.lineStrong }} />
          <button type="button" onClick={() => { setFrom(''); setTo(''); }} title="Effacer les dates"
            style={{ width: 40, height: 40, borderRadius: 9, border: `1.5px solid ${C.lineStrong}`,
              fontSize: 16, color: C.ink3, background: C.surface, cursor: 'pointer' }}>×</button>
        </div>
        {bornes.inverse && (
          <p style={{ fontSize: 12, color: C.ink2, marginTop: 6 }}>
            Les deux dates étaient inversées — elles ont été remises dans l’ordre.
          </p>
        )}
        {dateActive && (
          <p style={{ fontSize: 12, color: C.ink3, marginTop: 6 }}>
            Filtre par date actif : les périodes rapides sont désactivées.
          </p>
        )}
      </div>

      {erreur && <div style={{ ...S.err, marginBottom: 12 }}>{erreur}</div>}

      <div style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '9px 13px', borderBottom: `1px solid ${C.line}`, fontSize: 12.5, color: C.ink3 }}>
          {rows.length} bordereau{rows.length > 1 ? 'x' : ''} (signés, sortis sans signature ou annulés)
        </div>
        {rows.length === 0 && <div style={S.empty}>Aucune remise signée pour ces critères.</div>}
        {rows.map((b) => (
          <button key={b.id} type="button" onClick={() => setDetail(b)}
            style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, width: '100%', textAlign: 'left',
              alignItems: 'center', background: C.surface, border: 0, borderBottom: `1px solid ${C.line}`,
              padding: '12px 14px', cursor: 'pointer' }}>
            <span>
              <span style={{ fontWeight: 600, fontSize: 15, display: 'block' }}>{b.clientName}</span>
              <span style={{ fontSize: 12.5, color: C.ink3 }}>
                <b style={S.mono}>n° {b.numero}</b> · {b.parcels} colis · {b.handover?.driverName || (b.status === 'ANNULE' ? `annulé avant signature${b.annuleParNom ? ` par ${b.annuleParNom}` : ''}`
                  : b.status === 'SORTI_SANS_SIGNATURE' ? `sorti sans signature${b.sortiParNom ? ` par ${b.sortiParNom}` : ''}` : '—')}
              </span>
            </span>
            <span style={{ textAlign: 'right' }}>
              <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, color: '#fff',
                background: teinte(b.carrierName), whiteSpace: 'nowrap' }}>{b.carrierName}</span>
              <span style={{ ...S.mono, fontSize: 12, color: C.ink2, display: 'block', marginTop: 4 }}>
                {jour(b.handover?.signedAt || b.annuleLe || b.sortiLe)}
              </span>
            </span>
          </button>
        ))}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------
// 6. Détail d'un bordereau signé
// ---------------------------------------------------------------------
function Detail({ b, onRetour }) {
  const h = b.handover || {};
  if (!b.handover && b.status === 'SORTI_SANS_SIGNATURE') {
    const l = [['Transporteur', b.carrierName], ['Client', b.clientName], ['Bordereau', `n° ${b.numero}`], ['Colis', String(b.parcels)],
      ['Statut', 'Sorti sans signature du chauffeur'], ['Sorti le', dt(b.sortiLe)], ['Par', b.sortiParNom || '—'], ['Raison', b.sortiRaison || '—']];
    return (
      <>
        <Entete titre={b.clientName} sous={`${b.carrierName} · n° ${b.numero}`} onRetour={onRetour} />
        <div style={{ ...S.card, marginTop: 12 }}>
          <div style={{ ...S.k, marginBottom: 8 }}>Bordereau sorti sans signature</div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {l.map(([kk, v]) => (
              <li key={kk} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13, padding: '4px 0', borderBottom: `1px dotted ${C.line}` }}>
                <span>{kk}</span><b>{v}</b>
              </li>
            ))}
          </ul>
          <div style={{ fontSize: 11.5, color: C.ink3, marginTop: 8 }}>Pas de preuve signée : le chauffeur n’a pas signé.</div>
        </div>
      </>
    );
  }
  if (!b.handover && b.status === 'ANNULE') {
    const l = [['Transporteur', b.carrierName], ['Client', b.clientName], ['Bordereau', `n° ${b.numero}`], ['Colis', String(b.parcels)],
      ['Statut', 'Annulé avant signature'], ['Annulé le', dt(b.annuleLe)], ['Par', b.annuleParNom || '—'], ['Raison', b.annuleRaison || '—']];
    return (
      <>
        <Entete titre={b.clientName} sous={`${b.carrierName} · n° ${b.numero}`} onRetour={onRetour} />
        <div style={{ ...S.card, marginTop: 12 }}>
          <div style={{ ...S.k, marginBottom: 8 }}>Bordereau annulé</div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {l.map(([kk, v]) => (
              <li key={kk} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13, padding: '4px 0', borderBottom: `1px dotted ${C.line}` }}>
                <span>{kk}</span><b>{v}</b>
              </li>
            ))}
          </ul>
        </div>
      </>
    );
  }
  const lignes = [
    ['Transporteur', b.carrierName], ['Client', b.clientName],
    ['Bordereau', `n° ${b.numero}`], ['Colis', String(b.parcels)],
    ['Pages signées', String(b.pages ?? '—')],
    ['Chauffeur', h.driverName || '—'], ['Remis par', h.operatorName || '—'],
    ['Horodatage', dt(h.signedAt)], ['Empreinte', h.listHash || '—'],
    ['Réserves', h.reserves || 'Aucune'],
  ];
  return (
    <>
      <Entete titre={b.clientName} sous={`${b.carrierName} · n° ${b.numero}`} onRetour={onRetour} />
      <div style={{ ...S.card, marginTop: 12 }}>
        <div style={{ ...S.k, marginBottom: 8 }}>Preuve de remise</div>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {lignes.map(([k, v]) => (
            <li key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13,
              padding: '4px 0', borderBottom: `1px dotted ${C.line}` }}>
              <span>{k}</span>
              <b style={['Horodatage', 'Empreinte', 'Colis', 'Bordereau'].includes(k) ? S.mono : undefined}>{v}</b>
            </li>
          ))}
        </ul>
      </div>
      <a href={api.pdfUrl(b.id)} target="_blank" rel="noreferrer"
        style={{ ...S.big, marginTop: 13, textDecoration: 'none', display: 'block' }}>Ouvrir le PDF signé</a>
      <a href={api.pdfUrl(b.id, true)} target="_blank" rel="noreferrer"
        style={{ ...S.bigSec, marginTop: 9, textDecoration: 'none', display: 'block' }}>Ouvrir l’original non signé</a>
      {b.handoverId && <Impression handoverId={b.handoverId} />}
    </>
  );
}

// ---------------------------------------------------------------------
function Entete({ titre, sous, onRetour }) {
  return (
    <div style={{ ...S.card, display: 'flex', alignItems: 'center', gap: 10 }}>
      {onRetour && (
        <button type="button" onClick={onRetour} aria-label="Retour"
          style={{ width: 34, height: 34, borderRadius: 8, border: 0, background: C.surface2,
            color: C.ink2, fontSize: 19, cursor: 'pointer' }}>‹</button>
      )}
      <div>
        <div style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.2 }}>{titre}</div>
        <div style={{ fontSize: 11.5, color: C.ink3 }}>{sous}</div>
      </div>
    </div>
  );
}
